/**
 * Shipments Service
 * Handles shipment management operations
 */

import api from './api';

class ShipmentsService {
  /**
   * Get all shipments with filters
   * @param {Object} filters - Filter options
   */
  async getShipments(filters = {}) {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.status) params.append('status', filters.status);
    if (filters.serviceType) params.append('serviceType', filters.serviceType);
    if (filters.search) params.append('search', filters.search);
    if (filters.company) params.append('company', filters.company);
    if (filters.driver) params.append('driver', filters.driver);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.order) params.append('order', filters.order);

    const response = await api.get(`/shipment?${params.toString()}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch shipments');
  }

  /**
   * Get shipment by ID
   * @param {string} shipmentId - Shipment ID
   */
  async getShipmentById(shipmentId) {
    const response = await api.get(`/shipment/${shipmentId}`);

    if (response.success && response.data) {
      return response.data.shipment || response.data;
    }

    throw new Error('Failed to fetch shipment');
  }

  /**
   * Create new shipment
   * @param {Object} shipmentData - Shipment data
   */
  async createShipment(shipmentData) {
    const response = await api.post('/shipment', shipmentData);

    if (response.success && response.data) {
      return response.data.shipment || response.data;
    }

    throw new Error('Failed to create shipment');
  }

  /**
   * Update shipment
   * @param {string} shipmentId - Shipment ID
   * @param {Object} updates - Updated data
   */
  async updateShipment(shipmentId, updates) {
    const response = await api.put(`/shipment/${shipmentId}`, updates);

    if (response.success && response.data) {
      return response.data.shipment || response.data;
    }

    throw new Error('Failed to update shipment');
  }

  /**
   * Delete shipment
   * @param {string} shipmentId - Shipment ID
   */
  async deleteShipment(shipmentId) {
    const response = await api.delete(`/shipment/${shipmentId}`);

    if (response.success) {
      return true;
    }

    throw new Error('Failed to delete shipment');
  }

  /**
   * Get shipment statistics
   */
  async getStats() {
    const response = await api.get('/shipment/stats');

    if (response.success && response.data) {
      return response.data.stats || response.data;
    }

    throw new Error('Failed to fetch shipment stats');
  }

  /**
   * Update shipment status
   * @param {string} shipmentId - Shipment ID
   * @param {string} status - New status
   */
  async updateStatus(shipmentId, status) {
    return this.updateShipment(shipmentId, { status });
  }
}

const shipmentsService = new ShipmentsService();
export default shipmentsService;
